import { types } from "./types";
import {
  isGovernmentOpportunity,
  mspHardwareTotal,
  mspSoftwareTotal,
  targetPrice,
  isAllItemsWarranty,
  isMsaPriceFlagValid,
  theMsaPriceFlag,
  saasTargetTotal,
  flatSaasPriceItems,
  quoteForBackend
} from "../selectors";
import { MAX_QUANTITY } from "../../constants";
import { clamp } from "lodash-es";
import { fetchCategoryTree } from "../../api";
import hash from "object-hash";
import { validateQuote } from "../../api/quote"

export * from "./finance";

export const setQid = qid => ({
  type: types.SET_QUOTE_ID,
  payload: qid
});

export const setQuoteName = name => ({
  type: types.SET_QUOTE_NAME,
  payload: name
});

export const setPriceFlag = priceFlag => ({
  type: types.SET_QUOTE_PRICE_FLAG,
  payload: priceFlag
});

export const updatePriceFlag = () => (dispatch, getState) => {
  const state = getState();
  let priceFlag = state.quote.priceFlag;

  if (isAllItemsWarranty(state) || ( state.quote.isMsa && !isMsaPriceFlagValid(state))){
    priceFlag = 'CPQ_N/A';
  } else if(state.quote.isMsa && isMsaPriceFlagValid(state) ){
    // Use the configured price flag for MSA deals
    priceFlag = theMsaPriceFlag(state);
  } else if (!state.quote.validPriceFlagList.includes(state.quote.priceFlag)) {
    // Defaults to the first price flag option if the current price flag is not found
    priceFlag = state.quote.validPriceFlagList[0];
  }

  if(priceFlag !== state.quote.priceFlag){
    dispatch(setPriceFlag(priceFlag));
  }

  return {
    type: types.UPDATE_QUOTE_PRICE_FLAG,
    payload: priceFlag
  }
};

export const setValidPriceFlagList = validPriceFlagList => ({
  type: types.SET_VALID_QUOTE_PRICE_FLAG_LIST,
  payload: validPriceFlagList
});

export const setIsPrimary = isPrimary => ({
  type: types.SET_QUOTE_IS_PRIMARY,
  payload: isPrimary
});

export const setCustomerGroupCode = payload => ({
  type: types.SET_QUOTE_CUSTOMER_GROUP_CODE,
  payload
});

export const setCustomerGroupId = id => async (dispatch, getState) => {
  id = Number(id);

  dispatch({
    type: types.SET_QUOTE_CUSTOMER_GROUP_ID,
    payload: id
  });

  const { categoryTree, quote } = getState();
  const oldId = quote.customerGroupId;
  const isMsa = quote.isMsa;

  if (oldId !== id || categoryTree.length === 0) {
    dispatch({
      type: types.SET_CATEGORY_TREE,
      payload: await fetchCategoryTree(id, isMsa)
    });
  }
};

export const setOpportunityType = payload => ({
  type: types.SET_QUOTE_OPPORTUNITY_TYPE,
  payload
});

export const setTenderCode = payload => ({
  type: types.SET_QUOTE_TENDER_CODE,
  payload
});

export const setIsMsa = payload => ({
  type: types.SET_QUOTE_IS_MSA,
  payload
});

export const setSfdcCustomerNumber = payload => ({
  type: types.SET_QUOTE_SFDC_CUSTOMER_NUMBER,
  payload
});

export const setSoftwareDeliveryAddressId = softwareDeliveryAddressId => (dispatch, getState) => {
  dispatch({
    type: types.SET_QUOTE_SOFTWARE_DELIVERY_ADDRESS_ID,
    payload: softwareDeliveryAddressId
  })
  const state = getState()
  if(state.quote.sameSoftwareInstallDeliveryAddress === true) {
    dispatch(setSoftwareInstallationAddressId(softwareDeliveryAddressId))
  }
};

export const setSoftwareInstallationAddressId = softwareInstallationAddressId => ({
  type: types.SET_QUOTE_SOFTWARE_INSTALLATION_ADDRESS_ID,
  payload: softwareInstallationAddressId
});

export const setSaasDeliveryAddressId = deliveryAddressId => (dispatch, getState) => {
  dispatch({
    type: types.SET_QUOTE_SAAS_DELIVERY_ADDRESS_ID,
    payload: deliveryAddressId
  })
  const state = getState()
  if(state.quote.sameSaasInstallDeliveryAddress === true) {
    dispatch(setSaasInstallationAddressId(deliveryAddressId))
  }
};

export const setSaasInstallationAddressId = installationAddressId => ({
  type: types.SET_QUOTE_SAAS_INSTALLATION_ADDRESS_ID,
  payload: installationAddressId
});

export const setCustomerBillingAddressId = customerBillingAddressId => ({
  type: types.SET_QUOTE_CUSTOMER_BILLING_ADDRESS_ID,
  payload: customerBillingAddressId
});

export const addQuoteItemRaw = quoteItem => dispatch => {
  dispatch({
    type: types.ADD_QUOTE_ITEM,
    payload: quoteItem
  })
  dispatch(refreshSaasTargetTotal())
  dispatch(getQuoteBackendValidationMessages())
};

export const deleteQuoteItemRaw = quoteItem => dispatch => {
  dispatch({
    type: types.DELETE_QUOTE_ITEM,
    payload: quoteItem
  })
  dispatch(refreshSaasTargetTotal())
  dispatch(getQuoteBackendValidationMessages())
};

export const setQuoteItemRaw = quoteItem => ({
  type: types.SET_QUOTE_ITEM,
  payload: quoteItem
});

// Generate a quote item key used for determining inserting a separate
// line item or merely updating the quantity of the same item identified by
// the key in the quote when adding a product to quote.
function generateQuoteItemKey({
  productId,
  options,
  ccCampaigns,
  subscriptionTargetChargeValue,
  subscriptionUserNum,
  subscriptionTerm,
  underWarranty,
  ccBw,
  ccColour,
  scanCharge,
  scanningMonthlyCharge,
  flatMonthlyCharge,
  msaMetroClickChargeBw,
  msaMetroClickChargeColour,
  msaRegionalClickChargeBw,
  msaRegionalClickChargeColour,
  msaDefaultTerm,
  msaTerm,
  msaDefaultCfa,
  msaCfa,
  msaDefaultMdsCost,
  msaMdsCost,
  msaPriceFlag,
  msaPayable,
  msaInstalmentFrequency
}) {
  return hash(
    {
      productId,

      // Bundle options
      options,

      // Extra options
      ccCampaigns,
      subscriptionTargetChargeValue,
      subscriptionUserNum,
      subscriptionTerm,
      underWarranty,
      ccBw,
      ccColour,
      scanCharge,
      scanningMonthlyCharge,
      flatMonthlyCharge,
      // endregion

      // region MSA options
      msaMetroClickChargeBw,
      msaMetroClickChargeColour,
      msaRegionalClickChargeBw,
      msaRegionalClickChargeColour,
      msaDefaultTerm,
      msaTerm,
      msaDefaultCfa,
      msaCfa,
      msaDefaultMdsCost,
      msaMdsCost,
      msaPriceFlag,
      msaPayable,
      msaInstalmentFrequency
    },
    {
      unorderedArrays: true,
      unorderedObjects: true
    }
  );
}

export const addQuoteItem = (quoteItem, theQuoteItemToReplace = null) => (
  dispatch,
  getState
) => {
  const state = getState();

  const key = generateQuoteItemKey(quoteItem);

  const theSameQuoteItem = state.quote.items.find(item => item.key === key);

  const theQuoteItemToMerge = theQuoteItemToReplace || theSameQuoteItem;

  let qty;
  if (theQuoteItemToReplace) {
    qty = quoteItem.qty;
  } else if (theSameQuoteItem) {
    qty = theSameQuoteItem.qty + quoteItem.qty;
  } else {
    qty = quoteItem.qty;
  }

  const newQuoteItem = {
    ...quoteItem,

    key,
    qty,

    tradeIns: Array(qty)
      .fill([{ brand: "", model: "", serialNumber: "" }])
      .map(
        (item, index) =>
          (theQuoteItemToMerge && theQuoteItemToMerge.tradeIns[index]) || item
      ),
    deliveryAddressId: Array(qty)
      .fill(null)
      .map(
        (item, index) =>
          (theQuoteItemToMerge &&
            theQuoteItemToMerge.deliveryAddressId[index]) ||
          item
      ),
    installationAddressId: Array(qty)
      .fill(null)
      .map(
        (item, index) =>
          (theQuoteItemToMerge &&
            theQuoteItemToMerge.installationAddressId[index]) ||
          item
      ),
    isRegional: Array(qty)
      .fill(false)
      .map(
        (item, index) =>
          (theQuoteItemToMerge && theQuoteItemToMerge.isRegional[index]) || item
      ),
    purchaseOrders: Array(qty)
      .fill("")
      .map(
        (item, index) =>
          (theQuoteItemToMerge && theQuoteItemToMerge.purchaseOrders[index]) ||
          item
      ),
    costCenters: Array(qty)
      .fill("")
      .map(
        (item, index) =>
          (theQuoteItemToMerge && theQuoteItemToMerge.costCenters[index]) ||
          item
      ),

    isTheSameAddress: false,
    isTheSamePurchaseOrder: false,
    isTheSameCostCenter: false,

    // Mixing the default targetPrice
    // targetPrice: quoteItem.price,
    // options:
      // quoteItem.options &&
      // quoteItem.options.map(option => ({
        // ...option,
        // selections: option.selections.map(selection => ({
          // ...selection,
          // targetPrice: selection.price
        // }))
      // }))
  };

  // Remove the old quote item when update
  if (theQuoteItemToReplace) {
    dispatch(deleteQuoteItemRaw(theQuoteItemToReplace));
  }

  dispatch(addQuoteItemRaw(newQuoteItem));

  // The target price will be recalculated by the invocation below.
  dispatch(resetTargetTotals());

  //update price flag
  dispatch(updatePriceFlag());

  //set the saas target total
  dispatch(refreshSaasTargetTotal())
};

export const setQuoteItemQty = (quoteItem, qty) => dispatch => {
  qty = clamp(Math.round(qty), 1, MAX_QUANTITY);

  dispatch(
    setQuoteItemRaw({
      ...quoteItem,
      qty,

      tradeIns: Array(qty)
        .fill([
          {
            brand: "",
            model: "",
            serialNumber: ""
          }
        ])
        .map((item, index) => quoteItem.tradeIns[index] || item),
      deliveryAddressId: Array(qty)
        .fill(null)
        .map((item, index) => quoteItem.deliveryAddressId[index] || item),
      installationAddressId: Array(qty)
        .fill(null)
        .map((item, index) => quoteItem.installationAddressId[index] || item),
      isRegional: Array(qty)
        .fill(false)
        .map((item, index) => quoteItem.isRegional[index] || item),
      purchaseOrders: Array(qty)
        .fill("")
        .map((item, index) => quoteItem.purchaseOrders[index] || item),
      costCenters: Array(qty)
        .fill("")
        .map((item, index) => quoteItem.costCenters[index] || item),

      isTheSameAddress: false,
      isTheSamePurchaseOrder: false,
      isTheSameCostCenter: false
    })
  );

  dispatch(resetTargetTotals());
};

export const deleteQuoteItem = quoteItem => dispatch => {
  dispatch({
    type: types.DELETE_QUOTE_ITEM,
    payload: quoteItem
  });

  dispatch(resetTargetTotals());

  dispatch(updatePriceFlag())

  dispatch(refreshSaasTargetTotal())
};

export const setQuoteItemDeliveryAddress = (quoteItem, index, addrId) => {
  let deliveryAddressId = quoteItem.deliveryAddressId.map((value, localIndex) =>
    index === localIndex ? addrId : value
  );
  let installationAddressId = quoteItem.installationAddressId;

  if (quoteItem.isTheSameAddress) {
    deliveryAddressId = Array(quoteItem.qty).fill(addrId);
    installationAddressId = Array(quoteItem.qty).fill(addrId);
  }

  return setQuoteItemRaw({
    ...quoteItem,
    deliveryAddressId,
    installationAddressId
  });
};

export const setQuoteItemInstallationAddress = (quoteItem, index, addrId) => {
  let deliveryAddressId = quoteItem.deliveryAddressId;
  let installationAddressId = quoteItem.installationAddressId.map(
    (value, localIndex) => (index === localIndex ? addrId : value)
  );

  if (quoteItem.isTheSameAddress) {
    deliveryAddressId = Array(quoteItem.qty).fill(addrId);
    installationAddressId = Array(quoteItem.qty).fill(addrId);
  }

  return setQuoteItemRaw({
    ...quoteItem,
    deliveryAddressId,
    installationAddressId
  });
};

export const setQuoteItemIsRegional = (quoteItem, index, isRegional) =>
  setQuoteItemRaw({
    ...quoteItem,
    isRegional: quoteItem.isRegional.map((value, localIndex) =>
      index === localIndex || quoteItem.isTheSameAddress ? isRegional : value
    )
  });

export const setQuoteItemPurchaseOrder = (quoteItem, index, value) =>
  setQuoteItemRaw({
    ...quoteItem,
    purchaseOrders: quoteItem.purchaseOrders.map((localValue, localIndex) =>
      index === localIndex || quoteItem.isTheSamePurchaseOrder
        ? value
        : localValue
    )
  });

export const setQuoteItemCostCenter = (quoteItem, index, value) =>
  setQuoteItemRaw({
    ...quoteItem,
    costCenters: quoteItem.costCenters.map((localValue, localIndex) =>
      index === localIndex || quoteItem.isTheSameCostCenter ? value : localValue
    )
  });

export const setQuoteItemIsTheSameAddress = (quoteItem, isTheSame) =>
  setQuoteItemRaw({
    ...quoteItem,
    isTheSameAddress: isTheSame,
    deliveryAddressId: quoteItem.deliveryAddressId.map(addrId =>
      isTheSame ? quoteItem.deliveryAddressId[0] : addrId
    ),
    installationAddressId: quoteItem.installationAddressId.map(addrId =>
      isTheSame ? quoteItem.deliveryAddressId[0] : addrId
    ),
    isRegional: quoteItem.isRegional.map(isRegional =>
      isTheSame ? quoteItem.isRegional[0] : isRegional
    )
  });

export const setQuoteItemIsTheSamePurchaseOrder = (quoteItem, isTheSame) =>
  setQuoteItemRaw({
    ...quoteItem,
    isTheSamePurchaseOrder: isTheSame,
    purchaseOrders: quoteItem.purchaseOrders.map(item =>
      isTheSame ? quoteItem.purchaseOrders[0] : item
    )
  });

export const setQuoteItemIsTheSameCostCenter = (quoteItem, isTheSame) =>
  setQuoteItemRaw({
    ...quoteItem,
    isTheSameCostCenter: isTheSame,
    costCenters: quoteItem.costCenters.map(item =>
      isTheSame ? quoteItem.costCenters[0] : item
    )
  });

export const setQuoteItemTradeInField = (
  quoteItem,
  index,
  innerIndex,
  field,
  value,
  addresses = null
) => {
  if(field === "serialNumber")
  {
    //find if serial number matches in the addresses
    const matchingAddress = addresses.filter(address => address.SearchReference.map(sn => sn.SerialNumber).includes(value.toUpperCase()))
    //if so, update brand, update model, update address
    if(matchingAddress.length > 0)
    {
      var newQuoteItem = JSON.parse(JSON.stringify(quoteItem));
      //update addresses, if address are currently not selected
      if(newQuoteItem.deliveryAddressId[index] === "" || newQuoteItem.deliveryAddressId[index] === null) { newQuoteItem.deliveryAddressId[index] = matchingAddress[0].ID}
      if(newQuoteItem.installationAddressId[index] === "" || newQuoteItem.installationAddressId[index] === null) { newQuoteItem.installationAddressId[index] = matchingAddress[0].ID}
      const matchingSerial = matchingAddress[0].SearchReference.filter(sn => sn.SerialNumber === value.toUpperCase())

      //update brand and model if these are currently blank
      if(newQuoteItem.tradeIns[index][innerIndex].brand === "") {newQuoteItem.tradeIns[index][innerIndex].brand = matchingSerial[0].Brand}
      if(newQuoteItem.tradeIns[index][innerIndex].model === "") {newQuoteItem.tradeIns[index][innerIndex].model = matchingSerial[0].Model}

      //update serial number (since field === serialNumber)
      newQuoteItem.tradeIns[index][innerIndex].serialNumber = value
      //update state
      return setQuoteItemRaw(newQuoteItem)
    }
  }

  return setQuoteItemRaw({
    ...quoteItem,
    tradeIns: quoteItem.tradeIns.map((item, itemIdx) =>
      item.map((innerItem, innerItemIdx) =>
        index === itemIdx && innerIndex === innerItemIdx
          ? { ...innerItem, [field]: value }
          : innerItem
      )
    )
  })
};

export const addQuoteItemTradeIn = (quoteItem, index) =>
  setQuoteItemRaw({
    ...quoteItem,
    tradeIns: quoteItem.tradeIns.map((item, itemIdx) =>
      index === itemIdx
        ? [
            ...item,
            {
              brand: "",
              model: "",
              serialNumber: ""
            }
          ]
        : item
    )
  });

export const deleteQuoteItemTradeIn = (quoteItem, index, innerIndex) =>
  setQuoteItemRaw({
    ...quoteItem,
    tradeIns: quoteItem.tradeIns.map((item, itemIdx) =>
      item.filter(
        (innerItem, innerItemIdx) =>
          index !== itemIdx || innerIndex !== innerItemIdx
      )
    )
  });

export const setQuoteItemUserClickVolumeBw = (quoteItem, userClickVolumeBw) => dispatch => {
  userClickVolumeBw = Number(Math.abs(userClickVolumeBw).toFixed(0));
  return dispatch(setQuoteItemRaw({
    ...quoteItem,
    userClickVolumeBw
  }));
};

export const setQuoteItemUserClickVolumeColour = (quoteItem, userClickVolumeColour) => dispatch => {
  userClickVolumeColour = Number(Math.abs(userClickVolumeColour).toFixed(0));
  return dispatch(setQuoteItemRaw({
    ...quoteItem,
    userClickVolumeColour
  }));
};

const refreshTargetPrice = () => (dispatch, getState) => {
  const state = getState();
  state.quote.items
    // .filter(item => !item.isSaas)
    .map(item => ({
      ...item,
      options:
        item.options &&
        item.options.map(option => ({
          ...option,
          // Update the target price inside bundle options first
          selections: option.selections.map(selection => ({
            ...selection,
            targetPrice: selection.isSaas ? selection.targetPrice : targetPrice(
              state,
              selection,
              item.options.flatMap(option => option.selections),
              isGovernmentOpportunity(state)
            )
          }))
        }))
    }))
    .map(item => ({
      ...item,
      // Update the target price in the top level items
      targetPrice: item.options
        ? item.options
            .flatMap(option => option.selections)
            .filter(sel => !sel.isSaas)
            .reduce(
              (targetPrice, selection) =>
                targetPrice + selection.targetPrice * selection.qty,
              0
            )
        : targetPrice(state, item)
    }))
    .forEach(item => {
      dispatch({ type: types.SET_QUOTE_ITEM, payload: item });
    });
};

export const refreshSaasTargetTotal = () => (dispatch, getState) => {
  const state = getState();
  var saasTotal = saasTargetTotal(flatSaasPriceItems(state))
  dispatch(setTargetSaasTotal(saasTotal))
}

const resetTargetTotals = () => (dispatch, getState) => {
  const state = getState();

  // Reset the target totals
  dispatch(setTargetHardwareTotal(mspHardwareTotal(state)));
  dispatch(setTargetSoftwareTotal(mspSoftwareTotal(state)));
};

export const setTargetHardwareTotal = targetHardwareTotal => dispatch => {
  dispatch({
    type: types.SET_QUOTE_TARGET_HARDWARE_TOTAL,
    payload: targetHardwareTotal
  });

  dispatch(refreshTargetPrice());
};

export const setTargetSoftwareTotal = targetSoftwareTotal => dispatch => {
  dispatch({
    type: types.SET_QUOTE_TARGET_SOFTWARE_TOTAL,
    payload: targetSoftwareTotal
  });

  dispatch(refreshTargetPrice());
};

export const setTargetSaasTotal = targetSaasTotal => ({
  type: types.SET_QUOTE_TARGET_SAAS_TOTAL,
  payload: targetSaasTotal
});

export const setItemValidationMap = payload => ({
  type: types.SET_QUOTE_ITEM_VALIDATION_MAP,
  payload
});

export const setTonerRecycleBox = payload => ({
  type: types.SET_QUOTE_TONER_RECYCLE_BOX,
  payload
});

export const setServiceTerm = payload => ({
  type: types.SET_QUOTE_SERVICE_TERM,
  payload
});

export const setQuoteBackendValidationMessages = payload => ({
  type: types.SET_QUOTE_BACKEND_VALIDATION_MESSAGES,
  payload
});

export const getQuoteBackendValidationMessages = () => (dispatch, getState) => {
  const state = getState();
  dispatch(setQuoteBackendValidationInProgress(true))

  const quote = quoteForBackend(state)
  validateQuote({ ...quote, status: "PENDING" })
  .then(response => {
    const data = JSON.parse(response)
    dispatch(setQuoteBackendValidationInProgress(false))
    if(data.success) {
      dispatch(setQuoteBackendValidationMessages(data.errors))
    }
    else {
      dispatch(setQuoteBackendValidationMessages([
        {
          id: "",
          selectionIds: [],
          errorMessage: "Warning: Backend Validation of your quote has failed. There may be other validation errors not displayed"
        }
      ]))
    }
  });
}

export const setQuoteBackendValidationInProgress = payload => ({
  type: types.SET_QUOTE_BACKEND_VALIDATION_IN_PROGRESS,
  payload
});

export const setSameSoftwareInstallDeliveryAddress = payload => (dispatch, getState) => {
  dispatch({
    type: types.SET_QUOTE_SAME_SOFTWARE_INSTALL_DELIVERY_ADDRESS,
    payload
  })
  if(payload === true) {
    const state = getState()
    dispatch(setSoftwareInstallationAddressId(state.quote.softwareDeliveryAddressId))
  }
}

export const setSameSaasInstallDeliveryAddress = payload => (dispatch, getState) => {
  dispatch({
    type: types.SET_QUOTE_SAME_SAAS_INSTALL_DELIVERY_ADDRESS,
    payload
  })
  if(payload === true) {
    const state = getState()
    dispatch(setSaasInstallationAddressId(state.quote.saasDeliveryAddressId))
  }
}
